import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  DatePicker,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Spin,
  message,
  Divider,
  Switch
} from 'antd';
import {
  DownloadOutlined,
  PrinterOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import apiService from '@/services/apiService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface ProfitLossLineItem {
  accountId: string;
  accountCode: string;
  accountName: string;
  amount: number;
  previousAmount?: number;
  budgetAmount?: number;
  variance?: number;
  variancePercent?: number;
}

interface ProfitLossData {
  fromDate: string;
  toDate: string;
  previousFromDate?: string;
  previousToDate?: string;
  revenue: ProfitLossLineItem[];
  expenses: ProfitLossLineItem[];
  totalRevenue: number;
  totalExpenses: number;
  grossProfit: number;
  netIncome: number;
  previousTotalRevenue?: number;
  previousTotalExpenses?: number;
  previousNetIncome?: number;
}

const formatCurrency = (amount: number) => {
  if (amount === 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'HKD',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatPercent = (value: number | undefined) => {
  if (value === undefined || value === 0) return '-';
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const ProfitLossReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProfitLossData | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('year'),
    dayjs()
  ]);
  const [comparePrevious, setComparePrevious] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        fromDate: dateRange[0].format('YYYY-MM-DD'),
        toDate: dateRange[1].format('YYYY-MM-DD'),
        comparePreviousPeriod: comparePrevious.toString()
      });

      const response = await apiService.get(`/accounting/reports/profit-loss?${params}`);
      setData(response.data || response);
    } catch (error: any) {
      message.error(error.message || 'Failed to load profit & loss report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Open the General Ledger on the entries behind one figure.
   *
   * Same period as the report, so the ledger's movement over the period —
   * closing balance less opening balance — is the amount that was clicked.
   */
  const openLedgerFor = (accountId: string) => {
    const params = new URLSearchParams({
      accountId,
      fromDate: dateRange[0].format('YYYY-MM-DD'),
      toDate: dateRange[1].format('YYYY-MM-DD'),
    });
    navigate(`/accounting/general-ledger?${params.toString()}`);
  };

  const baseColumns: ColumnsType<ProfitLossLineItem> = [
    {
      title: 'Account Code',
      dataIndex: 'accountCode',
      key: 'accountCode',
      width: 120,
      render: (code: string, row) => (
        <a onClick={() => openLedgerFor(row.accountId)}>{code}</a>
      )
    },
    {
      title: 'Account Name',
      dataIndex: 'accountName',
      key: 'accountName',
      width: 250,
      render: (name: string, row) => (
        <a onClick={() => openLedgerFor(row.accountId)}>{name}</a>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (val: number, row) => (
        <a onClick={() => openLedgerFor(row.accountId)}>
          <strong>{formatCurrency(val)}</strong>
        </a>
      )
    }
  ];

  const compareColumns: ColumnsType<ProfitLossLineItem> = comparePrevious ? [
    {
      title: 'Previous Period',
      dataIndex: 'previousAmount',
      key: 'previousAmount',
      width: 150,
      align: 'right',
      render: (val: number) => formatCurrency(val || 0)
    },
    {
      title: 'Variance',
      dataIndex: 'variance',
      key: 'variance',
      width: 130,
      align: 'right',
      render: (val: number) => {
        if (!val) return '-';
        const color = val > 0 ? '#52c41a' : '#f5222d';
        return <Text style={{ color }}>{formatCurrency(val)}</Text>;
      }
    },
    {
      title: 'Change %',
      dataIndex: 'variancePercent',
      key: 'variancePercent',
      width: 100,
      align: 'right',
      render: (val: number) => {
        if (!val) return '-';
        const color = val > 0 ? '#52c41a' : '#f5222d';
        const Icon = val > 0 ? ArrowUpOutlined : ArrowDownOutlined;
        return (
          <Text style={{ color }}>
            <Icon /> {formatPercent(val)}
          </Text>
        );
      }
    }
  ] : [];

  const columns = [...baseColumns, ...compareColumns];

  const handleDateChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const handleExport = () => {
    if (!data) {
      message.warning('No data to export');
      return;
    }
    const rows: Record<string, any>[] = [];
    rows.push({ 'Category': 'REVENUE', 'Account Code': '', 'Account Name': '', 'Amount': '' });
    data.revenue.forEach(item => {
      rows.push({ 'Category': '', 'Account Code': item.accountCode, 'Account Name': item.accountName, 'Amount': Number(item.amount || 0) });
    });
    rows.push({ 'Category': '', 'Account Code': '', 'Account Name': 'Total Revenue', 'Amount': Number(data.totalRevenue || 0) });
    rows.push({ 'Category': '', 'Account Code': '', 'Account Name': '', 'Amount': '' });
    rows.push({ 'Category': 'EXPENSES', 'Account Code': '', 'Account Name': '', 'Amount': '' });
    data.expenses.forEach(item => {
      rows.push({ 'Category': '', 'Account Code': item.accountCode, 'Account Name': item.accountName, 'Amount': Number(item.amount || 0) });
    });
    rows.push({ 'Category': '', 'Account Code': '', 'Account Name': 'Total Expenses', 'Amount': Number(data.totalExpenses || 0) });
    rows.push({ 'Category': '', 'Account Code': '', 'Account Name': '', 'Amount': '' });
    rows.push({ 'Category': 'NET INCOME', 'Account Code': '', 'Account Name': '', 'Amount': Number(data.netIncome || 0) });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 30 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Profit & Loss');
    XLSX.writeFile(wb, `Profit_Loss_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    message.success('Profit & Loss Report exported');
  };

  const handlePrint = () => {
    window.print();
  };

  const getNetIncomeStatus = () => {
    if (!data) return null;
    if (data.netIncome > 0) {
      return <Tag color="success">Profit</Tag>;
    } else if (data.netIncome < 0) {
      return <Tag color="error">Loss</Tag>;
    }
    return <Tag>Break Even</Tag>;
  };

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>Profit & Loss Statement</Title>
          {getNetIncomeStatus()}
        </Space>
      }
      extra={
        <Space>
          <Space>
            <Text>Compare Previous:</Text>
            <Switch checked={comparePrevious} onChange={setComparePrevious} />
          </Space>
          <RangePicker
            value={dateRange}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
          />
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            Refresh
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export
          </Button>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        {data && (
          <>
            {/* Summary Statistics */}
            <Row gutter={24} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic
                  title="Total Revenue"
                  value={data.totalRevenue}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#52c41a' }}
                />
                {comparePrevious && data.previousTotalRevenue !== undefined && (
                  <Text type="secondary">
                    Previous: {formatCurrency(data.previousTotalRevenue)}
                  </Text>
                )}
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Expenses"
                  value={data.totalExpenses}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#f5222d' }}
                />
                {comparePrevious && data.previousTotalExpenses !== undefined && (
                  <Text type="secondary">
                    Previous: {formatCurrency(data.previousTotalExpenses)}
                  </Text>
                )}
              </Col>
              <Col span={6}>
                <Statistic
                  title="Gross Profit"
                  value={data.grossProfit}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: data.grossProfit >= 0 ? '#1890ff' : '#f5222d' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Net Income"
                  value={data.netIncome}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: data.netIncome >= 0 ? '#52c41a' : '#f5222d' }}
                />
                {comparePrevious && data.previousNetIncome !== undefined && (
                  <Text type="secondary">
                    Previous: {formatCurrency(data.previousNetIncome)}
                  </Text>
                )}
              </Col>
            </Row>

            <Divider />

            {/* Revenue Section */}
            <Title level={5} style={{ color: '#52c41a' }}>Revenue</Title>
            <Table
              columns={columns}
              dataSource={data.revenue}
              rowKey="accountId"
              pagination={false}
              bordered
              size="small"
              summary={() => (
                <Table.Summary>
                  <Table.Summary.Row style={{ backgroundColor: '#f6ffed' }}>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>Total Revenue</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong style={{ color: '#52c41a' }}>
                        {formatCurrency(data.totalRevenue)}
                      </Text>
                    </Table.Summary.Cell>
                    {comparePrevious && (
                      <>
                        <Table.Summary.Cell index={3} align="right">
                          {formatCurrency(data.previousTotalRevenue || 0)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          {formatCurrency(data.totalRevenue - (data.previousTotalRevenue || 0))}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          {data.previousTotalRevenue
                            ? formatPercent(((data.totalRevenue - data.previousTotalRevenue) / data.previousTotalRevenue) * 100)
                            : '-'}
                        </Table.Summary.Cell>
                      </>
                    )}
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />

            <Divider />

            {/* Expenses Section */}
            <Title level={5} style={{ color: '#f5222d' }}>Expenses</Title>
            <Table
              columns={columns}
              dataSource={data.expenses}
              rowKey="accountId"
              pagination={false}
              bordered
              size="small"
              summary={() => (
                <Table.Summary>
                  <Table.Summary.Row style={{ backgroundColor: '#fff2f0' }}>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>Total Expenses</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong style={{ color: '#f5222d' }}>
                        {formatCurrency(data.totalExpenses)}
                      </Text>
                    </Table.Summary.Cell>
                    {comparePrevious && (
                      <>
                        <Table.Summary.Cell index={3} align="right">
                          {formatCurrency(data.previousTotalExpenses || 0)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          {formatCurrency(data.totalExpenses - (data.previousTotalExpenses || 0))}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          {data.previousTotalExpenses
                            ? formatPercent(((data.totalExpenses - data.previousTotalExpenses) / data.previousTotalExpenses) * 100)
                            : '-'}
                        </Table.Summary.Cell>
                      </>
                    )}
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />

            <Divider />

            {/* Net Income Summary */}
            <Row justify="end" style={{ marginTop: 16 }}>
              <Col span={8}>
                <Card size="small" style={{ backgroundColor: data.netIncome >= 0 ? '#f6ffed' : '#fff2f0' }}>
                  <Statistic
                    title={<Text strong>Net Income</Text>}
                    value={data.netIncome}
                    precision={2}
                    prefix="$"
                    valueStyle={{
                      color: data.netIncome >= 0 ? '#52c41a' : '#f5222d',
                      fontSize: 28
                    }}
                  />
                </Card>
              </Col>
            </Row>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Text type="secondary">
                Period: {dayjs(data.fromDate).format('MMM DD, YYYY')} to {dayjs(data.toDate).format('MMM DD, YYYY')}
              </Text>
              {comparePrevious && data.previousFromDate && data.previousToDate && (
                <Text type="secondary" style={{ marginLeft: 16 }}>
                  | Compared to: {dayjs(data.previousFromDate).format('MMM DD, YYYY')} to {dayjs(data.previousToDate).format('MMM DD, YYYY')}
                </Text>
              )}
            </div>
          </>
        )}
      </Spin>
    </Card>
  );
};

export default ProfitLossReport;